import LegalLayout from '../components/layouts/LegalLayout';

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="October 13, 2026">
      <p>
        This Privacy Policy explains what information EextraMile (the "Service"), operated by Raj Motwani, collects, how we use it, and the
        choices you have. By using the Service, you agree to this Policy.
      </p>

      <h2>1. Information we collect</h2>
      <p><strong>Account information:</strong> your name and email address, obtained when you sign in with Google.</p>
      <p><strong>Profile information you provide:</strong> a background/resume summary, preferred email tone, and (optionally) an uploaded resume PDF, used to personalize outreach emails.</p>
      <p><strong>Gmail data (with your permission):</strong> we access your Gmail account to:</p>
      <ul>
        <li>Read incoming emails to detect job-application confirmations, recruiter replies, and bounce notifications.</li>
        <li>Create draft emails and, if you enable it, send emails directly from your account.</li>
        <li>Set up push notifications ("watch") so new relevant emails are detected automatically.</li>
      </ul>
      <p>
        We only read the content needed to classify an email as a job confirmation, a reply, or a bounce — we
        do not read or store the general contents of your inbox beyond what's needed for these features.
        Gmail OAuth tokens are encrypted at rest (Fernet encryption, key held in Google Cloud Secret Manager)
        and are never included in any API response returned to your browser.
      </p>
      <p><strong>Recruiter and company data:</strong> we look up publicly available information (name, job title, and a
        likely email address) about recruiters at companies you're applying to, using Google Custom Search and
        Hunter.io. This data is not personal information about you — it's cached and shared across all
        EextraMile users to avoid duplicate lookups of the same public information. If an email you send
        through the Service bounces or receives a reply, we record only whether that recruiter's address is
        valid in this shared cache, so other users aren't sent to an invalid address. The content of your
        emails is never stored in or shared through the cache.
      </p>
      <p><strong>Payment information:</strong> if you purchase credits, payment is processed by Razorpay. We do not
        store your card or payment details ourselves.
      </p>
      <p><strong>Usage data:</strong> basic application logs (e.g. request timestamps, error rates) for operating and
        securing the Service.
      </p>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To generate personalized outreach and follow-up emails using Anthropic's Claude AI models — your background summary, the recruiter's name/title, and the company/role are sent to Anthropic solely to draft that email text.</li>
        <li>To detect and classify job-application emails and recruiter replies in your Gmail account — for these emails only, the subject and the first part of the message body (up to 2,000 characters) are sent to Anthropic to classify them. Bounce notifications are detected without AI.</li>
        <li>To read the text of a resume PDF you upload, using OpenAI, so it can be used to personalize your emails.</li>
        <li>To operate the credits and subscription system, including processing payments.</li>
        <li>To send you service notifications (e.g. "drafts are ready," follow-up reminders) by email and in-app.</li>
        <li>To maintain and improve the Service, including debugging and security monitoring.</li>
      </ul>
      <p>We do not sell your personal information, and we do not use your Gmail content to serve you ads.</p>

      <h2>3. Google API Services User Data Policy</h2>
      <p>
        EextraMile's use and transfer to any other app of information received from Google APIs will adhere to
        the{' '}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Specifically, Gmail data accessed through the Service is
        used only to provide or improve the user-facing features described in this Policy — automatic
        detection of job-related emails, drafting/sending outreach on your instruction, and follow-up
        automation you explicitly enable. We do not use Gmail data for advertising, and we do not allow
        humans to read your Gmail data except: (a) with your affirmative consent for a specific support
        request, (b) if necessary for security purposes such as investigating abuse, (c) to comply with
        applicable law, or (d) where the data has been aggregated and anonymized.
      </p>
      <p>
        We do not use Gmail data to develop, improve, or train generalized artificial intelligence or
        machine-learning models, and the AI providers we send it to (listed in section 4) process it only to
        return a result to us, not to train their models.
      </p>

      <h2>4. Who we share data with</h2>
      <p>We share data only as needed to operate the Service:</p>
      <ul>
        <li><strong>Google</strong> — for sign-in and Gmail API access.</li>
        <li><strong>Anthropic</strong> — to generate email content and classify job-application emails and replies. Only the minimum text needed for that task is sent.</li>
        <li><strong>OpenAI</strong> — to read the text of resume PDFs you upload.</li>
        <li><strong>Google Custom Search, Hunter.io</strong> — to find and verify recruiter contact information.</li>
        <li><strong>Razorpay</strong> — to process payments.</li>
        <li><strong>Supabase</strong> — our database, authentication, and file storage provider, which hosts all of the above data.</li>
      </ul>
      <p>
        We do not share your personal information with any other third party for their own marketing
        purposes. We may disclose information if required by law or to protect the rights, property, or
        safety of EextraMile, our users, or the public.
      </p>

      <h2>5. Data retention and deletion</h2>
      <p>
        We retain your account data for as long as your account is active. You can permanently delete your
        account at any time from Settings → Danger Zone. Deleting your account revokes and deletes your
        Gmail OAuth token and permanently deletes your account record, profile, uploaded resume, job
        applications, notifications, and payment records stored with us. Our records of which recruiters
        you contacted are stripped of everything linking them to you (including Gmail message and thread
        IDs and any referral details); we keep only the fact that a recruiter was contacted on a given
        date, so we can enforce limits on how often any one recruiter is emailed. Recruiter/company contact
        data is not user-specific and is retained in the shared cache after your account is deleted, since
        it does not identify you.
      </p>

      <h2>6. Security</h2>
      <p>
        All data is encrypted in transit (HTTPS/TLS). Gmail OAuth tokens are additionally encrypted at rest,
        with the encryption key held in Google Cloud Secret Manager. Access to the underlying infrastructure is limited to
        authorized personnel. No method of transmission or storage is 100% secure, and we cannot guarantee
        absolute security.
      </p>

      <h2>7. Your rights</h2>
      <p>
        Depending on your location, you may have the right to access, correct, export, or delete your
        personal information, and to revoke EextraMile's access to your Google account at any time via your{' '}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
          Google Account permissions page
        </a>{' '}
        or from Settings within the Service. To exercise these rights, contact us at the email below.
      </p>

      <h2>8. Children's privacy</h2>
      <p>The Service is not directed to individuals under 18, and we do not knowingly collect information from them.</p>

      <h2>9. Changes to this Policy</h2>
      <p>
        We may update this Policy from time to time. Material changes will be reflected in the "Last updated"
        date above, and where required, communicated to your account email.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about this Policy or your data? Contact us at{' '}
        <a href="mailto:eextramileai@gmail.com">eextramileai@gmail.com</a>.
      </p>
    </LegalLayout>
  );
}
